/**
 * SVG-иконки (24×24, stroke=currentColor).
 *
 * Заменяют emoji в кнопках/табах/бейджах/статусах. Размер по умолчанию 20;
 * для inline-текста 16, для пустых состояний 24. Без `title` иконка
 * декоративная (`aria-hidden`), с `title` — смысловая (`role="img"`).
 */
import type { SVGProps } from 'react'

type IconSize = 16 | 18 | 20 | 24
export type IconProps = {
  size?: IconSize
  className?: string
  title?: string
}

function Svg({
  size = 20,
  className,
  title,
  children
}: IconProps & { children: React.ReactNode }) {
  const a11y = title
    ? { role: 'img' as const, 'aria-label': title }
    : { 'aria-hidden': true as const }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...(a11y as SVGProps<SVGSVGElement>)}
    >
      {children}
    </svg>
  )
}

export const Cash = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2.5" y="6" width="19" height="12" rx="2" />
    <circle cx="12" cy="12" r="2.6" />
    <path d="M6 9.5h.01M18 14.5h.01" />
  </Svg>
)

export const Card = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2.5" y="5" width="19" height="14" rx="2" />
    <path d="M2.5 9.5h19" />
  </Svg>
)

export const Qr = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M14 14h3v3h-3zM20 14h1M14 20h1M18 18h3v3" />
  </Svg>
)

export const Cart = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 4h2l2.2 10.2a2 2 0 0 0 2 1.6h7.4a2 2 0 0 0 2-1.6L20 7H6" />
    <circle cx="9.5" cy="19" r="1.2" />
    <circle cx="17" cy="19" r="1.2" />
  </Svg>
)

export const Scales = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3v18M7 21h10" />
    <path d="M4 7h16" />
    <path d="M4 7l-2 6a3 3 0 0 0 6 0L6 7M20 7l-2 6a3 3 0 0 0 6 0l-2-6" />
  </Svg>
)

export const Marking = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 8.5 8.5 4H18a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
    <path d="M8.5 4v4.5H4" />
    <path d="M9 14h6M9 17h4" />
  </Svg>
)

export const Box = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5z" />
    <path d="M3 7.5 12 12l9-4.5M12 12v9" />
  </Svg>
)

export const Check = (p: IconProps) => (
  <Svg {...p}>
    <path d="m4.5 12.5 5 5 10-11" />
  </Svg>
)

export const Alert = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3.5 22 20H2z" />
    <path d="M12 9v5M12 17.2v.1" />
  </Svg>
)

export const Info = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8v.1" />
  </Svg>
)

export const Refresh = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20 11a8 8 0 1 0-2.3 5.7" />
    <path d="M20 4v7h-7" />
  </Svg>
)

export const Search = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4.5 4.5" />
  </Svg>
)

export const Plus = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
)

export const Minus = (p: IconProps) => (
  <Svg {...p}>
    <path d="M5 12h14" />
  </Svg>
)

export const Trash = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 7h16M9 7V5h6v2M7 7l1 13h8l1-13" />
    <path d="M10 11v5M14 11v5" />
  </Svg>
)

export const Close = (p: IconProps) => (
  <Svg {...p}>
    <path d="m6 6 12 12M18 6 6 18" />
  </Svg>
)

export const ChevronDown = (p: IconProps) => (
  <Svg {...p}>
    <path d="m6 9 6 6 6-6" />
  </Svg>
)

export const Phone = (p: IconProps) => (
  <Svg {...p}>
    <rect x="6.5" y="2.5" width="11" height="19" rx="2" />
    <path d="M10.5 18.5h3" />
  </Svg>
)

export const Printer = (p: IconProps) => (
  <Svg {...p}>
    <path d="M7 9V3h10v6" />
    <rect x="3.5" y="9" width="17" height="7" rx="1.5" />
    <path d="M7 14h10v7H7z" />
  </Svg>
)

export const Power = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3v9" />
    <path d="M6.5 6.5a7.5 7.5 0 1 0 11 0" />
  </Svg>
)

export const CloudOff = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 3l18 18" />
    <path d="M7 18h9a4.5 4.5 0 0 0 3.2-7.7A6 6 0 0 0 8 7.5" />
    <path d="M6.3 9.4A5 5 0 0 0 7 18" />
  </Svg>
)

export const Pencil = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 20h4l10-10a2.8 2.8 0 0 0-4-4L4 16z" />
    <path d="m13.5 6.5 4 4" />
  </Svg>
)

export const Camera = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2L8 5h8l1.5 2h2A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5z" />
    <circle cx="12" cy="13" r="3.5" />
  </Svg>
)

export const Torch = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 2h6v5l-1.5 2v13h-3V9L9 7z" />
    <path d="M9 5H6M18 5h-3" />
  </Svg>
)

export const Clock = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.5 2" />
  </Svg>
)

export const Upload = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 16V4M7 9l5-5 5 5" />
    <path d="M4 20h16" />
  </Svg>
)

/** Солнце — светлая тема. */
export const Sun = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2.5v2M12 19.5v2M4.6 4.6l1.4 1.4M18 18l1.4 1.4M2.5 12h2M19.5 12h2M4.6 19.4L6 18M18 6l1.4-1.4" />
  </Svg>
)

/** Луна — тёмная тема. */
export const Moon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20 15.1A8.5 8.5 0 0 1 8.9 4a8.5 8.5 0 1 0 11.1 11.1Z" />
  </Svg>
)

/** Залитый кружок для статусов (🟢/🟡/🔴). Цвет — через className. */
export function Dot({ size = 16, className, title }: IconProps) {
  const a11y = title
    ? { role: 'img' as const, 'aria-label': title }
    : { 'aria-hidden': true as const }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      {...(a11y as SVGProps<SVGSVGElement>)}
    >
      <circle cx="12" cy="12" r="6" />
    </svg>
  )
}
